import moment from 'moment';

/**
 * Advanced CSV utilities for filename generation and content enhancement
 * Supports custom parameters, filter information, and CSV content manipulation
 * 
 * @param {Object} options - Configuration object for filename generation
 * @param {string} options.baseFileName - Base name for the file (e.g., 'Rescues', 'Donations', 'Reports')
 * @param {string} [options.receiverName] - Name of the receiving agency
 * @param {string} [options.donorName] - Name of the food donor
 * @param {string} [options.rescuerName] - Name of the rescuer
 * @param {string} [options.siteName] - Name of the site
 * @param {Object} [options.dateRange] - Date range object with start and end dates
 * @param {string|Date} [options.dateRange.start] - Start date
 * @param {string|Date} [options.dateRange.end] - End date
 * @param {string|Date} [options.dateRange.startDate] - Start date (alternative naming)
 * @param {string|Date} [options.dateRange.endDate] - End date (alternative naming)
 * @param {string} [options.dateRange.label] - Predefined label (e.g., 'This Month', 'Last 90 Days')
 * @param {string} [options.reportType] - Type of report (e.g., 'Volume', 'Past')
 * @param {Array<string>} [options.filters] - Array of applied filters
 * @param {string} [options.customSuffix] - Custom suffix to append
 * @param {boolean} [options.includeTimestamp] - Whether to include timestamp in filename
 * @param {string} [options.granularity] - Time granularity (e.g., 'daily', 'weekly', 'monthly')
 * 
 * @returns {string} Formatted CSV filename
 * 
 * @example
 * // Basic usage
 * generateCustomCsvFileName({ baseFileName: 'Rescues' })
 * // Returns: "Rescues.csv"
 * 
 * @example
 * // With receiver and date range
 * generateCustomCsvFileName({
 *   baseFileName: 'Past Rescues',
 *   receiverName: 'City Food Bank',
 *   dateRange: { start: '2024-01-01', end: '2024-01-31' }
 * })
 * // Returns: "Past Rescues - City Food Bank - Jan 01-31 2024.csv"
 */
export const generateCustomCsvFileName = (options = {}) => {
  const {
    baseFileName = 'Export',
    receiverName,
    donorName,
    rescuerName,
    siteName,
    dateRange,
    reportType,
    filters = [],
    customSuffix,
    includeTimestamp = false,
    granularity
  } = options;

  // Start with the base structure - no longer including "Food Rescue US"
  const parts = [];

  // Add report type if specified
  if (reportType) {
    parts.push(`${reportType} ${baseFileName}`);
  } else {
    parts.push(baseFileName);
  }

  // Add entity names in order of specificity
  if (siteName) {
    parts.push(siteName);
  }

  if (receiverName) {
    parts.push(receiverName);
  }

  if (donorName) {
    parts.push(donorName);
  }

  if (rescuerName) {
    parts.push(rescuerName);
  }

  // Add date range information
  if (dateRange) {
    const dateRangeString = formatDateRangeForFilename(dateRange, granularity);
    if (dateRangeString) {
      parts.push(dateRangeString);
    }
  }

  // Add filters if any
  if (filters.length > 0) {
    const filterString = filters.join(', ');
    parts.push(`(${filterString})`);
  }

  // Add custom suffix
  if (customSuffix) {
    parts.push(customSuffix);
  }

  // Add timestamp if requested
  if (includeTimestamp) {
    parts.push(moment().format('YYYY-MM-DD HH-mm'));
  }

  // Clean and join parts
  const cleanedParts = parts
    .filter(part => part && part.trim()) // Remove empty parts
    .map(part => sanitizeFilenamePart(part)); // Clean each part

  return `${cleanedParts.join(' - ')}.csv`;
};

/**
 * Format date range for filename inclusion
 * @param {Object} dateRange - Date range object
 * @param {string} [granularity] - Time granularity
 * @returns {string} Formatted date range string
 */
const formatDateRangeForFilename = (dateRange, granularity) => {
  if (!dateRange) return '';

  // If there's a predefined label, use it
  if (dateRange.label) {
    return dateRange.label;
  }

  // Support both naming conventions: start/end and startDate/endDate
  const startDate = dateRange.start || dateRange.startDate;
  const endDate = dateRange.end || dateRange.endDate;

  // If we have start and end dates, format them
  if (startDate && endDate) {
    const start = moment(startDate);
    const end = moment(endDate);

    // Check if it's the same day
    if (start.isSame(end, 'day')) {
      return start.format('MMM DD YYYY');
    }

    // Check if it's the same month
    if (start.isSame(end, 'month')) {
      return `${start.format('MMM DD')}-${end.format('DD YYYY')}`;
    }

    // Check if it's the same year
    if (start.isSame(end, 'year')) {
      return `${start.format('MMM DD')}-${end.format('MMM DD YYYY')}`;
    }

    // Different years
    return `${start.format('MMM DD YYYY')}-${end.format('MMM DD YYYY')}`;
  }

  // If only start date
  if (startDate) {
    return `Since ${moment(startDate).format('MMM DD YYYY')}`;
  }

  // If only end date
  if (endDate) {
    return `Until ${moment(endDate).format('MMM DD YYYY')}`;
  }

  return '';
};

/**
 * Sanitize a filename part by removing invalid characters
 * @param {string} part - The part to sanitize
 * @returns {string} Sanitized part
 */
const sanitizeFilenamePart = (part) => {
  if (!part || typeof part !== 'string') return '';
  
  return part
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
};

/**
 * Generate standardized report filename with entity type and date range
 * @param {string} reportName - Base report name
 * @param {Object} options - Options object
 * @param {Object} [options.dateRange] - Date range object
 * @param {string} [options.entityType] - Entity type for volume reports
 * @returns {string} Formatted filename without extension
 */
const generateReportFileName = (reportName, options = {}) => {
  const { dateRange, entityType } = options;
  const parts = [reportName];
  
  // Add entity type (for Volume Report)
  if (entityType) {
    const entityTypeMap = {
      'rescuers': 'Rescuers',
      'receivers': 'Receivers', 
      'food_donors': 'Food Donors'
    };
    const entityLabel = entityTypeMap[entityType] || entityType;
    parts.push(entityLabel);
  }
  
  // Add date range
  if (dateRange) {
    const dateRangeString = formatDateRangeForFilename(dateRange);
    if (dateRangeString) {
      parts.push(dateRangeString);
    }
  }
  
  const cleanedParts = parts
    .filter(part => part && part.trim())
    .map(part => sanitizeFilenamePart(part));
  
  return cleanedParts.join(' - ');
};

/**
 * Legacy function wrapper for backward compatibility
 * @param {string} fileName - Simple filename
 * @returns {string} Formatted filename
 */
export const formatCsvFileName = fileName => `${fileName}.csv`;

/**
 * Predefined filename generators for common use cases
 * 
 * Organized by category:
 * - Rescues-related exports (pastRescues, futureRescues)
 * - Donations-related exports (donations, donationRequests)
 * - People-related exports (rescuers, receivers, foodDonors, users)
 * - Communication-related exports (emails, sms)
 * - Report-related exports (volumeReport, growthReport, etc.)
 */
export const csvFileNameGenerators = {
  // Rescues-related exports
  pastRescues: (options = {}) => {
    const { receiverName, dateRange } = options;
    
    // Custom format for past rescues: {Receiver Name} Past Rescues - {Date Range}
    if (receiverName) {
      const parts = [receiverName, 'Past Rescues'];
      
      if (dateRange) {
        const dateRangeString = formatDateRangeForFilename(dateRange);
        if (dateRangeString) {
          parts.push(dateRangeString);
        }
      }
      
      const cleanedParts = parts
        .filter(part => part && part.trim())
        .map(part => sanitizeFilenamePart(part));
      
      return `${cleanedParts.join(' - ')}.csv`;
    }
    
    // Fallback to standard format if no receiver name
    return generateCustomCsvFileName({
      baseFileName: 'Past Rescues',
      ...options
    });
  },

  futureRescues: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Future Rescues',
    ...options
  }),

  // Donations-related exports
  donations: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Donations',
    ...options
  }),

  donationRequests: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Donation Requests',
    ...options
  }),

  // People-related exports
  rescuers: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Rescuers',
    ...options
  }),

  receivers: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Receiving Agencies',
    ...options
  }),

  foodDonors: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Food Donors',
    ...options
  }),

  users: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'People',
    ...options
  }),

  // Communication-related exports
  emails: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Sent Emails',
    ...options
  }),

  sms: (options = {}) => generateCustomCsvFileName({
    baseFileName: 'Sent SMS',
    ...options
  }),

  // Report-related exports with standardized patterns
  volumeReport: (options = {}) => generateReportFileName('Volume Report', options),

  growthReport: (options = {}) => generateReportFileName('Growth Report', options),

  rescuedFoodReport: (options = {}) => generateReportFileName('Rescued Food Report', options),

  receivingAgencyReport: (options = {}) => generateReportFileName('Receiving Agency Report', options),

  rescueReport: (options = {}) => generateReportFileName('Rescue Report', options)
};

/**
 * Helper function to extract common filename parameters from Redux state
 * @param {Object} state - Redux state object
 * @param {Object} [additionalOptions] - Additional options to merge
 * @returns {Object} Filename parameters object
 */
export const extractFilenameParamsFromState = (state, additionalOptions = {}) => {
  const params = {};

  // Extract site information
  if (state.app?.site?.name) {
    params.siteName = state.app.site.name;
  }

  // Extract current user information if needed
  if (state.app?.loggedInUser) {
    params.currentUser = state.app.loggedInUser;
  }

  return {
    ...params,
    ...additionalOptions
  };
};

/**
 * Generate filter information lines for CSV content
 * @param {Object} filterInfo - Filter information object
 * @param {Array} [filterInfo.activeFilters] - Array of active filter objects
 * @param {Object} [filterInfo.dateRange] - Date range filter
 * @param {string} [filterInfo.searchTerm] - Search term if any
 * @param {Object} [filterInfo.tableState] - MUI DataTable state object
 * @returns {string} Formatted filter information lines for CSV
 */
export const generateFilterInfoLines = (filterInfo = {}) => {
  const { activeFilters = [], dateRange, searchTerm, tableState } = filterInfo;
  const lines = [];

  // Extract filters from MUI DataTable state if provided
  let allFilters = [...activeFilters];
  if (tableState && tableState.filterList && tableState.columns) {
    const muiFilters = extractFiltersFromTableState(tableState);
    allFilters = [...allFilters, ...muiFilters];
  }

  // Add active filters
  if (allFilters.length > 0) {
    const filterStrings = allFilters.map(filter => {
      if (typeof filter === 'string') return filter;
      if (filter.label && filter.value) {
        return `${filter.label}: ${Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}`;
      }
      return filter.toString();
    });
    lines.push(`Filters Applied: ${filterStrings.join(', ')}`);
  }

  // Add date range
  if (dateRange) {
    const dateRangeString = formatDateRangeForDisplay(dateRange);
    if (dateRangeString) {
      lines.push(`Date Range: ${dateRangeString}`);
    }
  }

  // Add search term
  if (searchTerm && searchTerm.trim()) {
    lines.push(`Search: ${searchTerm.trim()}`);
  }

  // Add generation timestamp
  lines.push(`Generated: ${moment().format('MMM DD, YYYY [at] h:mm A')}`);

  return lines.length > 0 ? lines.join('\n') + '\n' : '';
};

/**
 * Extract filter information from MUI DataTable state
 * @param {Object} tableState - MUI DataTable state object
 * @returns {Array} Array of filter objects
 */
const extractFiltersFromTableState = (tableState) => {
  const filters = [];
  
  if (!tableState.filterList || !tableState.columns) {
    return filters;
  }

  tableState.filterList.forEach((filterValues, index) => {
    const column = tableState.columns[index];
    if (!column || !filterValues || filterValues.length === 0) {
      return;
    }

    // Handle array of filter values
    const values = Array.isArray(filterValues) ? filterValues : [filterValues];
    const nonEmptyValues = values.filter(val => val !== null && val !== undefined && val !== '');
    
    if (nonEmptyValues.length > 0) {
      filters.push({
        label: column.label || column.name || `Column ${index + 1}`,
        value: nonEmptyValues
      });
    }
  });

  return filters;
};

/**
 * Format date range for display in filter information
 * @param {Object} dateRange - Date range object
 * @returns {string} Formatted date range string
 */
const formatDateRangeForDisplay = (dateRange) => {
  if (!dateRange) return '';

  // If there's a predefined label, use it
  if (dateRange.label) {
    return dateRange.label;
  }

  // Support both naming conventions
  const startDate = dateRange.start || dateRange.startDate;
  const endDate = dateRange.end || dateRange.endDate;

  if (startDate && endDate) {
    const start = moment(startDate);
    const end = moment(endDate);

    // Check if it's the same day
    if (start.isSame(end, 'day')) {
      return start.format('MMM DD, YYYY');
    }

    // Check if it's the same month
    if (start.isSame(end, 'month')) {
      return `${start.format('MMM DD')}-${end.format('DD, YYYY')}`;
    }

    // Check if it's the same year
    if (start.isSame(end, 'year')) {
      return `${start.format('MMM DD')}-${end.format('MMM DD, YYYY')}`;
    }

    // Different years
    return `${start.format('MMM DD, YYYY')}-${end.format('MMM DD, YYYY')}`;
  }

  if (startDate) {
    return `Since ${moment(startDate).format('MMM DD, YYYY')}`;
  }

  if (endDate) {
    return `Until ${moment(endDate).format('MMM DD, YYYY')}`;
  }

  return '';
};

/**
 * Prepend filter information to CSV content
 * @param {string} csvContent - Original CSV content
 * @param {Object} filterInfo - Filter information object
 * @returns {string} CSV content with filter information prepended
 */
export const prependFilterInfoToCsv = (csvContent, filterInfo = {}) => {
  const filterLines = generateFilterInfoLines(filterInfo);
  
  if (!filterLines) {
    return csvContent;
  }

  // Convert filter lines to CSV format
  const commentedFilterLines = filterLines
    .split('\n')
    .filter(line => line.trim())
    .map(line => `"${line}"`)
    .join('\n');

  return `${commentedFilterLines}\n\n${csvContent}`;
};

export default generateCustomCsvFileName;
